import { createMemo } from "solid-js";
import { useNavigate, useSearch } from "@tanstack/solid-router";
import { useSignOut } from "@/providers/boot-manager";
import { AccountSwitcher } from "./account-switcher";

/**
 * Single, URL-driven mount of the {@link AccountSwitcher} overlay.
 *
 * Why a dedicated host: the app renders the sidebar TWICE (a mobile drawer and a
 * desktop rail). If each sidebar mounted its own `<AccountSwitcher>` Dialog, the
 * URL flag `?account=switch` would open BOTH at once → two stacked overlays, the
 * top one intercepting all pointer events (so "Add account" / "Continue as
 * guest" became unclickable). The switcher is addressable purely via the URL, so
 * we mount it ONCE here at the layout level; the per-sidebar trigger buttons just
 * flip the URL flag.
 */
export function AccountSwitcherHost() {
  const navigate = useNavigate();
  const layoutSearch = useSearch({ from: "/u/$u" });
  const { promptSignOut, promptClearData } = useSignOut();

  const isOpen = createMemo(() => layoutSearch().account === "switch");
  const setOpen = (open: boolean) => {
    void navigate({
      to: ".",
      search: (prev) => {
        const next = { ...(prev as Record<string, unknown>) };
        if (open) {
          next.account = "switch";
        } else {
          delete next.account;
        }
        return next;
      },
      replace: true,
    });
  };

  return (
    <AccountSwitcher
      open={isOpen()}
      onOpenChange={setOpen}
      onSignOut={() => {
        setOpen(false);
        promptSignOut();
      }}
      onClearData={() => {
        setOpen(false);
        promptClearData();
      }}
    />
  );
}
